import React from "react";
import { observer } from "mobx-react";
import ewtStore from "../EWTStore";
import { StickyTable, Row, Cell } from "react-sticky-table";
import RoundSelector from "./RoundSelector";
import TournamentSelector from "./TournamentSelector";
import PlayerCardHistoryTable from "./PlayerCardHistoryTable";
import { Modal, Button, Checkbox } from "antd";
import { formatRoundDesc } from "../dayjs";
import { theme, scoreColor, ewtModalClass, modalStyle, tableCellHeader, formatPlayerHandicap } from "../theme";

const addRoundDesc = (round) => {
  if (round.desc) return round;
  const copy = { ...round };
  copy.desc = formatRoundDesc(copy);
  return copy;
};

const roundsToMap = (rounds) => {
  const map = {};
  (rounds || []).forEach((round) => {
    map[round.id] = addRoundDesc(round);
  });
  return map;
};

const getDefaultRoundId = (history, tournamentID) => {
  const rounds = history.roundsByTournament[tournamentID] || [];
  if (rounds.length === 0) return null;
  const roundIds = new Set(rounds.map((round) => round.id));

  if (
    ewtStore.tournament &&
    ewtStore.tournament.id === tournamentID &&
    ewtStore.currentRoundID &&
    roundIds.has(ewtStore.currentRoundID)
  ) {
    return ewtStore.currentRoundID;
  }

  const tournament = history.tournaments.find((t) => t.id === tournamentID);
  if (
    tournament &&
    tournament.currentRoundID &&
    roundIds.has(tournament.currentRoundID)
  ) {
    return tournament.currentRoundID;
  }

  return addRoundDesc(rounds[0]).id;
};

const getPlayerHandicap = (storePlayer, player, tournamentID) => {
  const source = storePlayer || player;
  if (!source.handicaps || tournamentID == null || tournamentID === "all")
    return "";
  return source.handicaps[tournamentID];
};

const PlayerCard = observer(
  class PlayerCard extends React.Component {
    constructor(props) {
      super(props);
      this.state = {
        roundID: null,
        tournamentID: null,
        hidePracticeRounds: false,
        showGross: false,
        history: null,
        metaLoading: true,
        viewLoading: false,
        fullPP: false,
      };
      this.roundChanged = this.roundChanged.bind(this);
      this.tournamentChanged = this.tournamentChanged.bind(this);
    }

    componentDidMount() {
      this.loadInitial();
    }

    getDefaultTournamentID(history) {
      if (
        ewtStore.tournament &&
        history.tournaments.some((t) => t.id === ewtStore.tournament.id)
      ) {
        return ewtStore.tournament.id;
      }
      if (history.tournaments.length > 0) {
        return history.tournaments[0].id;
      }
      return "all";
    }

    async loadInitial() {
      const player = ewtStore.showPlayerCard;
      if (!player || !player.id) {
        this.setState({ metaLoading: false });
        return;
      }
      this.setState({ metaLoading: true });
      const history = await ewtStore.loadPlayerCardMeta(player.id);
      if (!history) {
        this.setState({ metaLoading: false, history: null });
        return;
      }

      const tournamentID = this.getDefaultTournamentID(history);
      this.setState({
        history,
        metaLoading: false,
        tournamentID,
        viewLoading: tournamentID !== "all",
      });

      if (tournamentID === "all") {
        await this.loadAllTournaments(history);
      } else {
        await this.loadTournament(tournamentID, history);
      }
    }

    async loadTournament(tournamentID, historyState) {
      const history = historyState || this.state.history;
      if (!history) return;
      this.setState({ viewLoading: true, tournamentID });
      const updated = await ewtStore.loadPlayerTournamentHistory(
        history,
        tournamentID
      );
      this.setState({
        history: updated,
        viewLoading: false,
        roundID: getDefaultRoundId(updated, tournamentID),
      });
    }

    async loadAllTournaments(historyState) {
      const history = historyState || this.state.history;
      if (!history) return;
      this.setState({ viewLoading: true, tournamentID: "all", roundID: null });
      const updated = await ewtStore.loadPlayerAllTournamentHistories(history);
      this.setState({
        history: updated,
        viewLoading: false,
        roundID: null,
      });
    }

    async tournamentChanged(tournamentID) {
      if (tournamentID === "all") {
        const history = this.state.history;
        const allLoaded =
          history &&
          history.tournaments.every(
            (tournament) => history.loadedTournaments[tournament.id]
          );
        if (allLoaded) {
          await ewtStore.ensurePlayerScores(history);
          if (ewtStore.tournament) {
            await ewtStore.loadPlayerTournamentHistory(
              history,
              ewtStore.tournament.id
            );
          }
          ewtStore.rebuildAllRounds(history);
          this.setState({ history, tournamentID: "all", roundID: null });
          return;
        }
        await this.loadAllTournaments();
        return;
      }
      if (
        this.state.history &&
        this.state.history.loadedTournaments[tournamentID]
      ) {
        this.setState({
          tournamentID,
          roundID: getDefaultRoundId(this.state.history, tournamentID),
        });
        return;
      }
      await this.loadTournament(tournamentID);
    }

    roundChanged(roundID) {
      this.setState({ roundID });
      const round =
        roundID && ewtStore.rounds && ewtStore.rounds[roundID];
      if (round && round.practice) {
        ewtStore.ensureRoundScoresLoaded(roundID);
      }
    }

    renderAllViewToolbar(history, tournamentID, inHeader) {
      return (
        <div
          className={`playerCardHistoryToolbar${
            inHeader ? " playerCardHistoryToolbar--inHeader" : ""
          }`}
        >
          <div className="playerCardHistoryTournament">
            <TournamentSelector
              tournamentID={tournamentID}
              tournaments={history.tournaments}
              onChange={this.tournamentChanged}
              compact
              inHeader={inHeader}
              allLabel="All Tourneys"
            />
          </div>
          <div className="playerCardHistoryChecks">
            <Checkbox
              checked={this.state.hidePracticeRounds}
              onChange={(e) =>
                this.setState({ hidePracticeRounds: e.target.checked })
              }
            >
              Hide Practice
            </Checkbox>
            <Checkbox
              checked={this.state.showGross}
              onChange={(e) =>
                this.setState({ showGross: e.target.checked })
              }
            >
              Gross
            </Checkbox>
          </div>
        </div>
      );
    }

    getLiveRoundScores(roundID, playerID) {
      if (
        ewtStore.tournament &&
        this.state.tournamentID === ewtStore.tournament.id &&
        ewtStore.roundScores &&
        ewtStore.roundScores[roundID] &&
        ewtStore.roundScores[roundID][playerID]
      ) {
        return ewtStore.roundScores[roundID][playerID];
      }
      return null;
    }

    buildSingleRoundTable(round, course, playerID, playerHandicap) {
      const liveScores = this.getLiveRoundScores(round.id, playerID);
      const historyScores =
        this.state.history &&
        this.state.history.computedScores[round.id];
      let frontPar = 0;
      let backPar = 0;
      let totalPar = 0;
      let frontScore = 0;
      let backScore = 0;
      let totalScore = 0;
      let frontNet = 0;
      let backNet = 0;
      let totalNet = 0;
      const holes = [];

      for (let i = 0; i < 18; i++) {
        const hole = { num: i + 1 };
        hole.par = course.holes[hole.num].tees[round.tees].par;
        totalPar += hole.par;

        let playerScore = null;
        if (liveScores && liveScores[hole.num]) {
          playerScore = liveScores[hole.num];
        } else if (historyScores && historyScores.holes[hole.num]) {
          const h = historyScores.holes[hole.num];
          playerScore = { score: h.score, strokes: h.strokes };
        }

        hole.score = playerScore ? playerScore.score : 0;
        totalScore += hole.score;
        hole.handicap = course.holes[hole.num].tees[round.tees].handicap;
        hole.net = playerScore ? hole.par + playerScore.strokes : 0;
        totalNet += hole.net;

        if (i < 9) {
          frontPar += hole.par;
          frontScore += hole.score;
          frontNet += hole.net;
        } else {
          backPar += hole.par;
          backScore += hole.score;
          backNet += hole.net;
        }
        holes[i] = hole;
        if (hole.score === 0) hole.score = "";
        if (hole.net === 0) hole.net = "";
      }

      if (frontScore === 0) frontScore = "";
      if (backScore === 0) backScore = "";
      if (backNet === 0) backNet = "";
      if (frontNet === 0) frontNet = "";
      if (totalScore === 0) totalScore = "";
      if (totalNet === 0) totalNet = "";

      const frontHoles = holes.slice(0, 9);
      frontHoles.push({
        num: "Out",
        score: frontScore,
        par: frontPar,
        net: frontNet,
      });
      const backHoles = holes.slice(9, 18);
      backHoles.push({
        num: "In",
        score: backScore,
        par: backPar,
        net: backNet,
      });
      backHoles.push({
        score: totalScore,
        num: "Tot",
        par: totalPar,
        net: totalNet,
      });

      return {
        allHoles: frontHoles.concat(backHoles),
        meta: liveScores || {},
      };
    }

    render() {
      const player = ewtStore.showPlayerCard;
      const {
        tournamentID,
        roundID,
        hidePracticeRounds,
        history,
        metaLoading,
        viewLoading,
      } = this.state;
      const isAllView = tournamentID === "all";
      const isLandscape = ewtStore.windowWidth > ewtStore.windowHeight;
      const isAllViewLandscape = isAllView && isLandscape;
      const allViewControlsInHeader = isAllViewLandscape && history;
      const storePlayer =
        ewtStore.players && ewtStore.players[player.id]
          ? ewtStore.players[player.id]
          : player;
      const historyPlayer =
        history && history.player ? history.player : storePlayer;
      const playerHandicap = isAllView
        ? ""
        : getPlayerHandicap(historyPlayer, player, tournamentID);

      const modalWidth = isAllView
        ? Math.min(ewtStore.windowWidth - 10, 1200)
        : undefined;

      if (metaLoading) {
        return (
          <Modal
            className={ewtModalClass}
            title={
              <div className="headerText">
                {player.first_name}&nbsp;{player.last_name}
              </div>
            }
            open={ewtStore.showPlayerCard != null}
            onCancel={() => (ewtStore.showPlayerCard = null)}
            footer={null}
            width={modalWidth}
            style={modalStyle}
          >
            <div style={{ padding: 10, fontSize: 18, color: theme.slate }}>Loading...</div>
          </Modal>
        );
      }

      let round = null;
      let course = null;
      let filteredRoundMap = {};
      let allHoles = [];
      let place = "";
      let netTotalStr = "";
      let netTotal = 0;

      if (!isAllView && history) {
        filteredRoundMap = roundsToMap(
          history.roundsByTournament[tournamentID] || []
        );
        round =
          roundID && filteredRoundMap[roundID]
            ? filteredRoundMap[roundID]
            : null;
        if (round) {
          course =
            (ewtStore.courses && ewtStore.courses[round.courseID]) ||
            history.coursesById[round.courseID];
        }
      }

      if (round && course && round.tees) {
        const tableData = this.buildSingleRoundTable(
          round,
          course,
          player.id,
          getPlayerHandicap(historyPlayer, player, tournamentID)
        );
        allHoles = tableData.allHoles;
        const meta = tableData.meta;
        place = meta.P || "";
        netTotal = meta.N != null ? meta.N : 0;
        netTotalStr = meta.NS || "";
      }

      if (
        !place &&
        !isAllView &&
        ewtStore.tournament &&
        tournamentID === ewtStore.tournament.id &&
        ewtStore.sortedPlayers
      ) {
        const ranked = ewtStore.sortedPlayers.find((p) => p.id === player.id);
        if (ranked && ranked.position) {
          place = ranked.position;
          if (ranked.total != null && ranked.total !== 9999) {
            netTotal = ranked.total;
          }
          if (ranked.totalStr) netTotalStr = ranked.totalStr;
        }
      }

      const filteredHistoryRounds =
        history &&
        history.allRounds.filter(
          (round) => !hidePracticeRounds || !round.practice
        );

      const displayScores =
        isAllView && history
          ? ewtStore.buildPlayerCardDisplayScores(
              history,
              player.id,
              filteredHistoryRounds
            )
          : history && history.computedScores
          ? { ...history.computedScores }
          : {};

      const cellHeader = tableCellHeader;

      const modalBodyHeight = isAllView
        ? ewtStore.windowHeight - (allViewControlsInHeader ? 56 : 72)
        : ewtStore.windowHeight - 130;

      return (
        <Modal
          className={`${ewtModalClass}${
            isAllViewLandscape ? " ewtModal--allTourneysLandscape" : ""
          }`}
          title={
            <div
              className="headerText"
              style={{
                width: "100%",
                padding: 0,
                margin: 0,
                paddingRight: 28,
              }}
            >
              <div
                className={`flexRow${
                  allViewControlsInHeader ? " playerCardHistoryHeaderRow" : ""
                }`}
                style={{
                  width: "100%",
                  alignItems: "center",
                  gap: allViewControlsInHeader ? 0 : 12,
                  flexWrap: allViewControlsInHeader ? "nowrap" : undefined,
                }}
              >
                {allViewControlsInHeader ? (
                  <>
                    <div className="headerText playerCardHistoryHeaderName">
                      {player.first_name}&nbsp;
                      {player.last_name}&nbsp;&nbsp;
                      {playerHandicap !== "" &&
                        playerHandicap !== undefined && (
                          <> ({formatPlayerHandicap(playerHandicap)})</>
                        )}
                    </div>
                    {this.renderAllViewToolbar(history, tournamentID, true)}
                  </>
                ) : (
                  <>
                <div
                  className="flexColumn flexGrow"
                  style={{ minWidth: 0 }}
                >
                  <div className="headerText">
                    {player.first_name}&nbsp;
                    {player.last_name}&nbsp;&nbsp;
                    {playerHandicap !== "" && playerHandicap !== undefined && (
                      <> ({formatPlayerHandicap(playerHandicap)})</>
                    )}
                  </div>
                  {!isAllView && place && (
                    <div style={{ marginTop: 4, fontSize: 20, fontWeight: 500 }}>
                      {place}
                      &nbsp;Place&nbsp;
                      {netTotalStr !== "" && (
                        <span style={{ color: scoreColor(netTotal) }}>
                          ({netTotalStr})
                        </span>
                      )}
                    </div>
                  )}
                </div>
                {!isAllView && storePlayer.pictureURL && (
                  <div
                    style={{
                      flexShrink: 0,
                      width: 64,
                      height: 64,
                      borderRadius: 8,
                      overflow: "hidden",
                      backgroundColor: "rgba(255, 255, 255, 0.12)",
                    }}
                  >
                    {!this.state.fullPP && (
                      <img
                        src={storePlayer.pictureURL}
                        alt=""
                        style={{
                          cursor: "pointer",
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          display: "block",
                        }}
                        onClick={() =>
                          this.setState({ fullPP: !this.state.fullPP })
                        }
                      />
                    )}
                  </div>
                )}
                {!isAllView &&
                  !storePlayer.pictureURL &&
                  ewtStore.player &&
                  ewtStore.showPlayerCard.id === ewtStore.player.id && (
                    <Button
                      onClick={() => {
                        ewtStore.showPlayerCard = null;
                        ewtStore.gallery = false;
                        ewtStore.showView = "photo";
                      }}
                    >
                      Add Picture
                    </Button>
                  )}
                  </>
                )}
              </div>
            </div>
          }
          open={ewtStore.showPlayerCard != null}
          onCancel={() => (ewtStore.showPlayerCard = null)}
          bodyStyle={{
            margin: 0,
            padding: 0,
            backgroundColor: theme.surface,
            ...(isAllViewLandscape
              ? {
                  flex: 1,
                  minHeight: 0,
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                }
              : { height: modalBodyHeight }),
          }}
          style={
            isAllViewLandscape
              ? { top: 0, margin: 0, paddingBottom: 0, maxWidth: "100vw" }
              : modalStyle
          }
          width={isAllViewLandscape ? ewtStore.windowWidth : modalWidth}
          closable={true}
          footer={null}
        >
          <div
            className="flexColumn flexGrow"
            style={
              isAllViewLandscape
                ? {
                    flex: 1,
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                    padding: "0 8px",
                  }
                : {
                    height: modalBodyHeight,
                    padding: isAllView ? "2px 8px 5px" : 5,
                  }
            }
          >
            {this.state.fullPP && storePlayer.pictureURL && (
              <div
                className="playerPhotoOverlay"
                onClick={() => this.setState({ fullPP: false })}
              >
                <img
                  src={storePlayer.pictureURL}
                  alt=""
                  onClick={() => this.setState({ fullPP: false })}
                />
              </div>
            )}

            {!this.state.fullPP && history && (
              <>
                {isAllView ? (
                  !allViewControlsInHeader &&
                  this.renderAllViewToolbar(history, tournamentID, false)
                ) : (
                  <div
                    className="flexRow"
                    style={{ gap: 8, alignItems: "stretch", width: "100%" }}
                  >
                    <div style={{ flex: "0 1 130px", minWidth: 0, maxWidth: "40%" }}>
                      <TournamentSelector
                        tournamentID={tournamentID}
                        tournaments={history.tournaments}
                        onChange={this.tournamentChanged}
                        fillWidth
                        allLabel="All Tourneys"
                      />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <RoundSelector
                        roundID={roundID}
                        rounds={filteredRoundMap}
                        handleEvent={this.roundChanged}
                        fillWidth
                      />
                    </div>
                  </div>
                )}
              </>
            )}

            {!this.state.fullPP && isAllView && history && (
              <div
                className="flexColumn playerCardHistoryHost"
                style={{
                  flex: 1,
                  marginTop: isAllViewLandscape ? 0 : 2,
                  minHeight: 0,
                  overflow: "hidden",
                }}
              >
                {viewLoading ? (
                  <div style={{ padding: 10, fontSize: 20 }}>Loading rounds...</div>
                ) : (
                  <PlayerCardHistoryTable
                    rounds={filteredHistoryRounds}
                    computedScores={displayScores}
                    showGross={this.state.showGross}
                    coursesById={history.coursesById}
                  />
                )}
              </div>
            )}

            {!this.state.fullPP &&
              !isAllView &&
              viewLoading && (
                <div style={{ padding: 10, fontSize: 18 }}>Loading rounds...</div>
              )}

            {!this.state.fullPP &&
              !isAllView &&
              !viewLoading &&
              round &&
              course &&
              round.tees && (
                <StickyTable
                  border={1}
                  style={{ fontSize: 19, marginTop: 5, width: "100%" }}
                  leftStickyColumnCount={0}
                >
                  <Row style={{ width: "100%" }}>
                    <Cell style={cellHeader}>Hole</Cell>
                    <Cell style={cellHeader}>Par</Cell>
                    <Cell style={cellHeader}>
                      <b>Score</b>
                    </Cell>
                    <Cell style={cellHeader}>Hcp</Cell>
                    <Cell style={cellHeader}>Net</Cell>
                  </Row>
                  {allHoles.map((hole, index) => (
                    <Row key={index}>
                      <Cell
                        style={{
                          border: "1px solid lightgray",
                          textAlign: "center",
                        }}
                      >
                        {hole.num}
                      </Cell>
                      <Cell
                        style={{
                          border: "1px solid lightgray",
                          textAlign: "center",
                          padding: 3,
                        }}
                      >
                        {hole.par}
                      </Cell>
                      <Cell
                        style={{
                          border: "1px solid lightgray",
                          textAlign: "center",
                          padding: 3,
                          cursor: "pointer",
                        }}
                        onDoubleClick={() => {
                          if (ewtStore.player && ewtStore.player.admin) {
                            hole.roundID = round.id;
                            hole.playerID = ewtStore.showPlayerCard.id;
                            ewtStore.correctScore = hole;
                          }
                        }}
                      >
                        <span
                          style={{
                            color:
                              hole.score !== "" && hole.score < hole.par
                                ? "red"
                                : "black",
                          }}
                        >
                          <b>{hole.score}</b>
                        </span>
                      </Cell>
                      <Cell
                        style={{
                          border: "1px solid lightgray",
                          textAlign: "center",
                          padding: 3,
                        }}
                      >
                        {hole.handicap}
                      </Cell>
                      <Cell
                        style={{
                          border: "1px solid lightgray",
                          textAlign: "center",
                          padding: 3,
                        }}
                      >
                        <span
                          style={{
                            color:
                              hole.net !== "" && hole.net < hole.par
                                ? "red"
                                : "black",
                          }}
                        >
                          {hole.net}
                        </span>
                      </Cell>
                    </Row>
                  ))}
                </StickyTable>
              )}

            {!this.state.fullPP &&
              !isAllView &&
              !viewLoading &&
              (!round || !course || !round.tees) && (
                <div style={{ padding: 10, fontSize: 20 }}>
                  No round data available for this player.
                </div>
              )}
          </div>
        </Modal>
      );
    }
  }
);

export default PlayerCard;
